/**
 * Lightweight toast notifications for VIVA HUB / Scheduler admin UI.
 */

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

const ToastContext = createContext(null)

const DEFAULT_DURATION = {
  success: 4500,
  error: 7000,
  info: 4500,
}

let toastId = 0

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timersRef = useRef(new Map())

  const dismiss = useCallback((id) => {
    const timer = timersRef.current.get(id)
    if (timer) {
      window.clearTimeout(timer)
      timersRef.current.delete(id)
    }
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback(
    ({ type = 'info', message, duration } = {}) => {
      if (!message) return null
      const id = ++toastId
      const ms = duration ?? DEFAULT_DURATION[type] ?? DEFAULT_DURATION.info

      setToasts((prev) => [...prev.slice(-2), { id, type, message }])

      if (ms > 0) {
        const timer = window.setTimeout(() => dismiss(id), ms)
        timersRef.current.set(id, timer)
      }

      return id
    },
    [dismiss]
  )

  const success = useCallback(
    (message, options) => toast({ ...options, type: 'success', message }),
    [toast]
  )
  const error = useCallback(
    (message, options) => toast({ ...options, type: 'error', message }),
    [toast]
  )
  const info = useCallback(
    (message, options) => toast({ ...options, type: 'info', message }),
    [toast]
  )

  const value = useMemo(
    () => ({
      toast,
      success,
      error,
      info,
      dismiss,
      toasts,
    }),
    [toast, success, error, info, dismiss, toasts]
  )

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return ctx
}
